// web/ts/app.ts
// The paindiary TS island: interactive body map. Front and back are shown side
// by side (no toggle) so the whole picture is visible at once; shared zoom
// 0.9-16x, pan/pinch, day navigation, copy-previous-day, point drop at
// normalized (u,v), heat-colored intensity, entry panel/bottom sheet.
// Talks JSON to /api/body/*; every mutation carries the CSRF header.
import { SHAPES, VBW, VBH } from './shapes';

type Point = {
  id: string;
  side: 'front' | 'back';
  u: number;
  v: number;
  region_code: string | null;
  region_label: string | null;
  intensity: number;
  qualities: string[];
  note: string;
  daypart: string;   // 'am' | 'pm' | 'unspecified'
  by: string;        // author display: "You" | first name | "Imported"
  by_kind: 'you' | 'caregiver' | 'import';
};

type Day = { day: string; label: string; count: number };
type SideKey = 'front' | 'back';
const SIDES: SideKey[] = ['front', 'back'];

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector(sel);
  if (!el) throw new Error('missing ' + sel);
  return el as T;
};

const root = document.getElementById('bodymap-root');
if (root) initBodyMap(root);

function heat(i: number): string {
  if (i < 2) return '#8BD46B';
  if (i < 4) return '#FFC23F';
  if (i < 6) return '#FF9A3D';
  if (i < 8) return '#FF6B4A';
  return '#FF3B5C';
}
function intensityWord(i: number): string {
  if (i === 0) return 'none';
  if (i <= 3) return 'mild';
  if (i <= 6) return 'moderate';
  if (i <= 8) return 'severe';
  return 'extreme';
}

const POS = ['burning', 'pins_and_needles', 'shooting', 'aching', 'throbbing'];
const NEG = ['numbness', 'sensory_loss', 'weakness'];
const QUALITY_LABEL: Record<string, string> = {
  burning: 'burning', pins_and_needles: 'pins & needles', shooting: 'shooting',
  aching: 'aching', throbbing: 'throbbing', numbness: 'numbness',
  sensory_loss: 'sensory loss', weakness: 'weakness',
};

function csrf(): string {
  const m = document.querySelector('meta[name="csrf"]');
  return m ? (m as HTMLMetaElement).content : '';
}
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

async function api<T>(method: string, url: string, body?: unknown): Promise<T> {
  const resp = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text.trim() || resp.statusText);
  }
  return resp.json() as Promise<T>;
}

function toast(msg: string) {
  document.querySelectorAll('.toast').forEach((t) => t.remove());
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2200);
}

function initBodyMap(root: HTMLElement) {
  // ---- state ----
  let zoom = 1, panX = 0, panY = 0;
  let days: Day[] = [];
  let dayIdx = 0;
  let points: Point[] = [];
  let selected: { id: string | null; side: SideKey; u: number; v: number; daypart: string } | null = null;
  let formI = 5;
  let formQ: string[] = [];
  // Look-back time of day: AM pass, PM pass, or All. New points get this tag
  // ('all' stores 'unspecified'); the body filters to the active daypart.
  let daypart: 'am' | 'pm' | 'all' = 'am';
  const dpTag = (): string => (daypart === 'all' ? 'unspecified' : daypart);
  const dpMatch = (p: Point): boolean => daypart === 'all' || p.daypart === daypart;
  // Current copy-from configuration, recomputed in renderAll; null = nothing to copy.
  let copyCfg: { from: string; from_dayparts: string[]; to_daypart: string; label: string } | null = null;
  // Day-summary (daily-log) state.
  let dlADL: string[] = [];
  let dlTriggers: string[] = [];
  let dlAdlOpts: { code: string; label: string }[] = [];
  let dlTrigOpts: { code: string; label: string }[] = [];

  const svgNS = 'http://www.w3.org/2000/svg';
  const viewport = $('#bm-viewport');
  // Front and back each have their own svg, shapes and points layers; clicks on
  // each place points with that side.
  const fig: Record<SideKey, { svg: SVGSVGElement; shapes: SVGGElement; points: SVGGElement; count: HTMLElement }> = {
    front: {
      svg: document.getElementById('bm-svg-front') as unknown as SVGSVGElement,
      shapes: document.getElementById('bm-shapes-front') as unknown as SVGGElement,
      points: document.getElementById('bm-points-front') as unknown as SVGGElement,
      count: $('#bm-count-front'),
    },
    back: {
      svg: document.getElementById('bm-svg-back') as unknown as SVGSVGElement,
      shapes: document.getElementById('bm-shapes-back') as unknown as SVGGElement,
      points: document.getElementById('bm-points-back') as unknown as SVGGElement,
      count: $('#bm-count-back'),
    },
  };
  const panel = $('#bm-panel');
  const listEl = $('#bm-list');
  const listCount = $('#bm-list-count');
  const dayLabelEl = $('#bm-day-label');
  const copyBtn = $<HTMLButtonElement>('#bm-copy-prev');

  // ---- render silhouettes ----
  function renderShapes(side: SideKey) {
    const layer = fig[side].shapes;
    layer.innerHTML = '';
    const shapes = SHAPES[side];
    for (const key of Object.keys(shapes)) {
      const p = document.createElementNS(svgNS, 'path');
      p.setAttribute('d', shapes[key]);
      p.setAttribute('data-region', key);
      // Transparent fill keeps the region clickable (isPointInFill tests the
      // geometry) while the anatomical PNG shows through; a faint stroke hints
      // the region boundaries on the light figure.
      p.setAttribute('fill', 'transparent');
      p.setAttribute('stroke', 'rgba(20,24,34,0.12)');
      p.setAttribute('stroke-width', '2');
      layer.appendChild(p);
    }
  }
  function renderShapesAll() { for (const s of SIDES) renderShapes(s); }

  // Region hit-test for a side: exact point-in-fill against its atlas paths.
  function regionAt(side: SideKey, x: number, y: number): string | null {
    const svg = fig[side].svg;
    const pt = svg.createSVGPoint();
    pt.x = x; pt.y = y;
    const paths = fig[side].shapes.querySelectorAll('path');
    for (const p of Array.from(paths)) {
      if ((p as SVGGeometryElement).isPointInFill(pt)) {
        return p.getAttribute('data-region');
      }
    }
    return null;
  }

  // ---- render points (both sides) ----
  function renderPoints() {
    for (const side of SIDES) {
      const layer = fig[side].points;
      layer.innerHTML = '';
      const sidePoints = points.filter((p) => p.side === side && dpMatch(p));
      for (const p of sidePoints) {
        const cx = p.u * VBW, cy = p.v * VBH;
        const r = 34 + p.intensity * 5;
        const halo = document.createElementNS(svgNS, 'circle');
        halo.setAttribute('cx', String(cx));
        halo.setAttribute('cy', String(cy));
        halo.setAttribute('r', String(r * 1.7));
        halo.setAttribute('fill', heat(p.intensity));
        halo.setAttribute('opacity', '0.22');
        layer.appendChild(halo);

        const dot = document.createElementNS(svgNS, 'circle');
        dot.setAttribute('cx', String(cx));
        dot.setAttribute('cy', String(cy));
        dot.setAttribute('r', String(r));
        dot.setAttribute('fill', heat(p.intensity));
        dot.setAttribute('data-point-id', p.id);
        dot.style.cursor = 'pointer';
        const title = document.createElementNS(svgNS, 'title');
        title.textContent = `${p.region_label || 'point'} · ${p.intensity}/10`;
        dot.appendChild(title);
        layer.appendChild(dot);

        const label = document.createElementNS(svgNS, 'text');
        label.setAttribute('x', String(cx));
        label.setAttribute('y', String(cy));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('dominant-baseline', 'central');
        label.setAttribute('font-size', '46');
        label.setAttribute('font-weight', '800');
        label.setAttribute('fill', '#0E0E10');
        label.setAttribute('pointer-events', 'none');
        label.textContent = String(p.intensity);
        layer.appendChild(label);
      }
      if (selected && selected.side === side) {
        const ring = document.createElementNS(svgNS, 'circle');
        ring.setAttribute('cx', String(selected.u * VBW));
        ring.setAttribute('cy', String(selected.v * VBH));
        ring.setAttribute('r', '70');
        ring.setAttribute('fill', 'none');
        ring.setAttribute('stroke', '#2FD37B');
        ring.setAttribute('stroke-width', '6');
        ring.setAttribute('stroke-dasharray', '18 14');
        layer.appendChild(ring);
      }
      fig[side].count.textContent = `${side === 'back' ? 'Back' : 'Front'} · ${sidePoints.length}`;
    }
  }

  function renderList() {
    listEl.innerHTML = '';
    const shown = points.filter(dpMatch);
    listCount.textContent = String(shown.length);
    for (const p of shown) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'bm-row';
      const q = p.qualities.map((x) => QUALITY_LABEL[x] || x).join(', ') || 'no quality';
      const badgeClass = p.by_kind === 'you' ? 'badge-soft' : p.by_kind === 'import' ? 'badge-warn' : 'badge-info';
      row.innerHTML =
        `<span class="bm-dot" style="background:${heat(p.intensity)}">${p.intensity}</span>` +
        `<span class="grow"><span class="bm-region">${escapeHtml(p.region_label || 'point')}</span>` +
        `<span class="bm-q">${escapeHtml(q)}${p.note ? ' · “' + escapeHtml(p.note) + '”' : ''}</span></span>` +
        `<span class="badge ${badgeClass}">${escapeHtml(p.by)}</span>` +
        `<span class="bm-side">${p.side.toUpperCase()}</span>`;
      row.addEventListener('click', () => editPoint(p));
      listEl.appendChild(row);
    }
  }

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  // ---- zoom / pan (shared across both figures) ----
  function applyTransform() {
    const stage = $('#bm-stage');
    stage.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
  }
  function setZoom(next: number, cx?: number, cy?: number) {
    const prev = zoom;
    zoom = Math.min(16, Math.max(0.9, next));
    if (cx !== undefined && cy !== undefined) {
      const rect = viewport.getBoundingClientRect();
      const ox = cx - rect.left - rect.width / 2 - panX;
      const oy = cy - rect.top - rect.height / 2 - panY;
      panX -= ox * (zoom / prev - 1);
      panY -= oy * (zoom / prev - 1);
    }
    applyTransform();
  }

  let dragging = false, moved = false, lastX = 0, lastY = 0;
  viewport.addEventListener('pointerdown', (e) => {
    dragging = true; moved = false;
    lastX = e.clientX; lastY = e.clientY;
  });
  viewport.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastX, dy = e.clientY - lastY;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
    if (moved) {
      panX += dx; panY += dy;
      lastX = e.clientX; lastY = e.clientY;
      applyTransform();
    }
  });
  window.addEventListener('pointerup', () => { dragging = false; });
  viewport.addEventListener('wheel', (e) => {
    e.preventDefault();
    setZoom(zoom * (e.deltaY < 0 ? 1.16 : 1 / 1.16), e.clientX, e.clientY);
  }, { passive: false });
  viewport.addEventListener('dblclick', (e) => setZoom(zoom * 2.1, e.clientX, e.clientY));

  // pinch
  let pinchDist = 0;
  viewport.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const d = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY);
      if (pinchDist > 0) {
        setZoom(zoom * (d / pinchDist),
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2);
      }
      pinchDist = d;
    }
  }, { passive: false });
  viewport.addEventListener('touchend', () => { pinchDist = 0; });

  $('#bm-zoom-in').addEventListener('click', () => setZoom(zoom * 1.3));
  $('#bm-zoom-out').addEventListener('click', () => setZoom(zoom / 1.3));
  $('#bm-zoom-reset').addEventListener('click', () => { zoom = 1; panX = 0; panY = 0; applyTransform(); });

  // ---- click to drop / edit (per figure) ----
  for (const side of SIDES) {
    fig[side].svg.addEventListener('click', (e) => {
      if (moved) return; // it was a drag
      const target = e.target as Element;
      const pid = target.getAttribute && target.getAttribute('data-point-id');
      if (pid) {
        const p = points.find((x) => x.id === pid);
        if (p) editPoint(p);
        return;
      }
      const svg = fig[side].svg;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const loc = pt.matrixTransform(ctm.inverse());
      if (loc.x < 0 || loc.y < 0 || loc.x > VBW || loc.y > VBH) return;
      if (!regionAt(side, loc.x, loc.y)) return; // only on the body
      selected = { id: null, side, u: loc.x / VBW, v: loc.y / VBH, daypart: dpTag() };
      formI = 5; formQ = [];
      openPanel('');
      renderPoints();
    });
  }

  function editPoint(p: Point) {
    selected = { id: p.id, side: p.side, u: p.u, v: p.v, daypart: p.daypart };
    formI = p.intensity;
    formQ = [...p.qualities];
    openPanel(p.note);
    renderPoints();
  }

  // ---- entry panel ----
  function openPanel(note: string) {
    if (!selected) return;
    const editing = selected.id !== null;
    $('#bm-panel-title').textContent =
      (editing ? 'Edit point · ' : 'New pain point · ') + selected.side;
    const region = regionAt(selected.side, selected.u * VBW, selected.v * VBH);
    const dp = selected.daypart === 'unspecified' ? '' : ' · ' + selected.daypart.toUpperCase();
    $('#bm-panel-sub').textContent =
      (regionLabel(region) || 'point') + ' · ' + (days[dayIdx]?.label || '') + dp;
    ($('#bm-note') as HTMLTextAreaElement).value = note;
    ($('#bm-intensity') as HTMLInputElement).value = String(formI);
    $('#bm-remove').textContent = editing ? 'Remove' : 'Cancel';
    syncIntensity();
    renderChips();
    panel.classList.add('open');
  }
  function closePanel() {
    panel.classList.remove('open');
    selected = null;
    renderPoints();
  }
  function regionLabel(code: string | null): string | null {
    if (!code) return null;
    const el = document.querySelector(`#bm-region-labels [data-code="${code}"]`);
    return el ? el.getAttribute('data-label') : code;
  }

  function syncIntensity() {
    $('#bm-i-num').textContent = String(formI);
    ($('#bm-i-num') as HTMLElement).style.color = heat(formI);
    $('#bm-i-word').textContent = intensityWord(formI);
    ($('#bm-i-word') as HTMLElement).style.color = heat(formI);
  }
  $('#bm-intensity').addEventListener('input', (e) => {
    formI = Number((e.target as HTMLInputElement).value);
    syncIntensity();
  });

  function renderChips() {
    for (const [holder, codes] of [['#bm-chips-pos', POS], ['#bm-chips-neg', NEG]] as const) {
      const wrap = $(holder);
      wrap.innerHTML = '';
      for (const code of codes) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'chip' + (formQ.includes(code) ? ' on' : '');
        b.textContent = QUALITY_LABEL[code];
        b.addEventListener('click', () => {
          formQ = formQ.includes(code) ? formQ.filter((x) => x !== code) : [...formQ, code];
          renderChips();
        });
        wrap.appendChild(b);
      }
    }
  }

  $('#bm-close').addEventListener('click', closePanel);
  $('#bm-remove').addEventListener('click', async () => {
    if (!selected) return;
    if (selected.id) {
      try {
        await api('DELETE', `/api/body/points/${selected.id}`);
        toast('Point removed');
      } catch (err) {
        toast(String(err));
        return;
      }
      await loadDays(days[dayIdx]?.day);
    }
    closePanel();
  });

  $('#bm-save').addEventListener('click', async () => {
    if (!selected) return;
    const body = {
      side: selected.side, u: selected.u, v: selected.v,
      region_code: regionAt(selected.side, selected.u * VBW, selected.v * VBH),
      intensity: formI, qualities: formQ,
      note: ($('#bm-note') as HTMLTextAreaElement).value,
      daypart: selected.daypart,
      day: days[dayIdx]?.day, tz: TZ,
    };
    try {
      if (selected.id) {
        await api('PUT', `/api/body/points/${selected.id}`, body);
      } else {
        await api('POST', '/api/body/points', body);
      }
      toast('Saved');
    } catch (err) {
      toast(String(err));
      return;
    }
    await loadDays(days[dayIdx]?.day);
    closePanel();
  });

  // ---- day controls ----
  // AM / PM / All look-back toggle: filters the figures to the active daypart
  // and tags new points with it.
  document.querySelectorAll('[data-daypart]').forEach((btn) => {
    btn.addEventListener('click', () => {
      daypart = btn.getAttribute('data-daypart') as 'am' | 'pm' | 'all';
      document.querySelectorAll('[data-daypart]').forEach((b) =>
        b.classList.toggle('on', b === btn));
      renderAll();
    });
  });

  async function loadDays(keep?: string) {
    const res = await api<{ days: Day[] }>('GET', `/api/body/days?tz=${encodeURIComponent(TZ)}`);
    days = res.days;
    // Default to YESTERDAY (the morning look-back), which the server always
    // includes as the second-to-last day; today is last.
    dayIdx = Math.max(0, days.length - 2);
    if (keep) {
      const i = days.findIndex((d) => d.day === keep);
      if (i >= 0) dayIdx = i;
    }
    await loadPoints();
  }
  async function loadPoints() {
    const d = days[dayIdx];
    if (!d) { points = []; renderAll(); return; }
    const res = await api<{ points: Point[] }>(
      'GET', `/api/body/points?day=${d.day}&tz=${encodeURIComponent(TZ)}`);
    points = res.points || [];
    renderAll();
    await loadDailyLog(d.day);
  }
  function renderAll() {
    const d = days[dayIdx];
    dayLabelEl.textContent = d ? d.label : ',';
    ($('#bm-day-prev') as HTMLButtonElement).disabled = dayIdx <= 0;
    ($('#bm-day-next') as HTMLButtonElement).disabled = dayIdx >= days.length - 1;
    const prev = days[dayIdx - 1];
    const amCount = points.filter((p) => p.daypart === 'am').length;
    const pmCount = points.filter((p) => p.daypart === 'pm').length;

    // Copy seeds the active pass: AM from the previous day, PM from this
    // morning's AM, All from the previous whole day. Guarded so a second click
    // cannot duplicate (the server enforces the same).
    copyCfg = null;
    let hint = 'No earlier day';
    if (daypart === 'pm') {
      if (amCount > 0 && pmCount === 0 && d) {
        copyCfg = { from: d.day, from_dayparts: ['am'], to_daypart: 'pm', label: 'Copy PM from this morning' };
      } else {
        hint = amCount === 0 ? 'Log AM first' : 'PM already started';
      }
    } else if (daypart === 'am') {
      if (prev && amCount === 0) {
        copyCfg = { from: prev.day, from_dayparts: ['am', 'unspecified'], to_daypart: 'am', label: `Copy AM from ${prev.label}` };
      } else if (prev) {
        hint = 'AM already started';
      }
    } else {
      if (prev && points.length === 0) {
        copyCfg = { from: prev.day, from_dayparts: [], to_daypart: 'unspecified', label: `Copy from ${prev.label}` };
      } else if (prev) {
        hint = 'Day already started';
      }
    }
    copyBtn.disabled = !copyCfg;
    copyBtn.textContent = copyCfg ? copyCfg.label : hint;
    renderShapesAll();
    renderPoints();
    renderList();
  }
  $('#bm-day-prev').addEventListener('click', async () => { if (dayIdx > 0) { dayIdx--; await loadPoints(); } });
  $('#bm-day-next').addEventListener('click', async () => { if (dayIdx < days.length - 1) { dayIdx++; await loadPoints(); } });
  copyBtn.addEventListener('click', async () => {
    const d = days[dayIdx];
    if (!d || !copyCfg) return;
    try {
      await api('POST', '/api/body/copy-prev', {
        day: d.day, from: copyCfg.from, tz: TZ,
        from_dayparts: copyCfg.from_dayparts, to_daypart: copyCfg.to_daypart,
      });
      toast('Copied');
      await loadDays(d.day);
    } catch (err) {
      toast(String(err));
    }
  });

  // ---- day summary (per-day whole-person look-back) ----
  async function loadDailyLog(day: string) {
    const res = await api<{
      adl_options: { code: string; label: string }[];
      trigger_options: { code: string; label: string }[];
      sleep_disrupted: boolean; stiffness: string;
      adl_codes: string[]; trigger_codes: string[]; note: string;
    }>('GET', `/api/body/daily-log?day=${day}&tz=${encodeURIComponent(TZ)}`);
    dlAdlOpts = res.adl_options || [];
    dlTrigOpts = res.trigger_options || [];
    dlADL = res.adl_codes || [];
    dlTriggers = res.trigger_codes || [];
    ($('#dl-sleep') as HTMLInputElement).checked = !!res.sleep_disrupted;
    ($('#dl-stiffness') as HTMLSelectElement).value = res.stiffness || 'none';
    ($('#dl-note') as HTMLTextAreaElement).value = res.note || '';
    renderDLChips();
  }
  function renderDLChips() {
    for (const [holder, opts, sel] of [
      ['#dl-adl', dlAdlOpts, dlADL],
      ['#dl-triggers', dlTrigOpts, dlTriggers],
    ] as const) {
      const wrap = $(holder);
      wrap.innerHTML = '';
      for (const o of opts) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'chip' + (sel.includes(o.code) ? ' on' : '');
        b.textContent = o.label;
        b.addEventListener('click', () => {
          const i = sel.indexOf(o.code);
          if (i >= 0) sel.splice(i, 1); else sel.push(o.code);
          renderDLChips();
        });
        wrap.appendChild(b);
      }
    }
  }
  $('#dl-save').addEventListener('click', async () => {
    const d = days[dayIdx];
    if (!d) return;
    try {
      await api('POST', '/api/body/daily-log', {
        day: d.day,
        sleep_disrupted: ($('#dl-sleep') as HTMLInputElement).checked,
        stiffness: ($('#dl-stiffness') as HTMLSelectElement).value,
        adl_codes: dlADL, trigger_codes: dlTriggers,
        note: ($('#dl-note') as HTMLTextAreaElement).value,
      });
      toast('Day summary saved');
    } catch (err) {
      toast(String(err));
    }
  });

  applyTransform();
  loadDays().catch((err) => toast(String(err)));
}
